import React, { forwardRef, useRef, FC, MouseEventHandler, RefAttributes, FocusEventHandler } from 'react';
import { time } from 'uniqid';

export type ButtonVariant =
  | 'PRIMARY'
  | 'SECONDARY'
  | 'TERTIARY'
  | 'DELETE'
  | 'RESET'
  | 'ADV_SEARCH'
  | 'EDIT'
  | 'DOWNLOAD'
  | 'PRINT'
  | 'MAIL'
  | 'CANCEL'
  | 'REVIEW'
  | 'REFRESH'
  | 'EXPORT';
const buttonClasses = {
  PRIMARY: 'btn-primary',
  SECONDARY: 'btn-secondary',
  TERTIARY: 'btn-tertiary',
  DELETE: 'btn-delete',
  RESET: 'btn-reset',
  ADV_SEARCH: 'btn-adv-search',
  EDIT: 'btn-edit',
  DOWNLOAD: 'btn-download',
  PRINT: 'btn-print',
  MAIL: 'btn-mail',
  CANCEL: 'btn-cancel',
  REVIEW: 'btn-review',
  REFRESH: 'btn-refresh',
  EXPORT: 'btn-export'
};
const tertiaryButtons = [
  'TERTIARY',
  'DELETE',
  'RESET',
  'ADV_SEARCH',
  'EDIT',
  'DOWNLOAD',
  'PRINT',
  'MAIL',
  'CANCEL',
  'REVIEW',
  'REFRESH',
  'EXPORT'
];

export type ButtonProps = {
  id?: string;
  ariaLabel?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  onClick?: MouseEventHandler;
  type?: 'submit' | 'button';
  className?: string;
  variant?: ButtonVariant;
  onBlur?: FocusEventHandler;
  onFocus?: FocusEventHandler;
  // ariaDisabled removed; use disabled for both attributes
  tabIndex?: number;
};

export const Button: FC<ButtonProps & RefAttributes<HTMLButtonElement>> = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'PRIMARY',
      id = time('button-id-'),
      ariaLabel,
      disabled = false,
      onClick,
      type = 'button',
      className = '',
      onBlur = () => true,
      onFocus = () => true,
    // ariaDisabled removed
      tabIndex
    },
    ref
  ) => {
    const buttonRef = useRef(null);
    const refDom = ref || buttonRef;
    const buttonClassName = buttonClasses[variant] ? buttonClasses[variant] : buttonClasses.PRIMARY;
    return (
      <button
        ref={refDom}
        id={id}
        aria-label={ariaLabel}
        onClick={!disabled ? onClick : () => null}
        disabled={disabled}
        type={type}
        onBlur={onBlur}
        onFocus={onFocus}
        className={`btn ${buttonClassName} ${className}`}
        data-testid={id}
        {...(tabIndex !== undefined ? { tabIndex } : {})}
        aria-disabled={disabled ? 'true' : 'false'}>
        {tertiaryButtons.indexOf(variant) !== -1 && <span aria-hidden="true" />}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
export default Button;
